import { Card, H2, Pill } from '../ui.jsx';
import { C, fmt, fmtK, retCol, incK } from '../lib/format.js';
import { INC, INC_LABEL, DAS } from '../lib/finance.js';

export default function RetireGrid({ retGrid, income, setIncome, deathAge, setDA, retYr, retSnap, L }) {
  const { isNarrow, g2 } = L;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: g2, gap: 12 }}>
        <Card>
          <div style={{ fontSize: 10, color: C.lbl, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>Income Target (2026 real)</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {INC.map(t => <Pill key={t} label={incK(t) + (INC_LABEL[t] ? ' ' + INC_LABEL[t] : '')} active={income === t} onClick={() => setIncome(t)} />)}
          </div>
        </Card>
        <Card>
          <div style={{ fontSize: 10, color: C.lbl, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>Planning to Joanne's age</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {DAS.map(da => <Pill key={da} label={da} active={deathAge === da} onClick={() => setDA(da)} color={C.teal} />)}
          </div>
        </Card>
      </div>

      <Card>
        <H2 sub="Earliest year to retire · tap any cell">Earliest Viable Retirement Year</H2>
        <div className="scroll-x">
          <table style={{ width: '100%', borderSpacing: 4, borderCollapse: 'separate', minWidth: 560 }}>
            <thead>
              <tr>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: 10, color: C.lbl, textTransform: 'uppercase', letterSpacing: 1 }}>Target</th>
                {DAS.map(da => <th key={da} style={{ padding: '8px 6px', textAlign: 'center', fontSize: 10, color: C.lbl, textTransform: 'uppercase', letterSpacing: 1 }}>Age {da}</th>)}
              </tr>
            </thead>
            <tbody>
              {INC.map(inc => (
                <tr key={inc}>
                  <td style={{ padding: '4px 10px', fontFamily: "'DM Mono',monospace", fontSize: 14, fontWeight: 600, color: C.bright, whiteSpace: 'nowrap' }}>
                    {incK(inc)}
                    {INC_LABEL[inc] && <div style={{ fontSize: 9, color: C.teal, fontWeight: 600, fontFamily: "'DM Sans',sans-serif" }}>{INC_LABEL[inc]}</div>}
                  </td>
                  {DAS.map(da => {
                    const yr = retGrid[inc][da], col = retCol(yr);
                    const act = inc === income && da === deathAge;
                    return (
                      <td key={da} style={{ padding: 3 }}>
                        <div onClick={() => { setIncome(inc); setDA(da); }} style={{
                          borderRadius: 8, padding: '10px 4px', textAlign: 'center', cursor: 'pointer', minHeight: 52,
                          display: 'flex', flexDirection: 'column', justifyContent: 'center',
                          background: col + '1A', border: `${act ? 2 : 1}px solid ${col}${act ? 'CC' : '44'}`,
                        }}>
                          <div style={{ fontSize: 15, fontWeight: 700, color: col, fontFamily: "'DM Mono',monospace" }}>{yr >= 2049 ? '49+' : yr}</div>
                          <div style={{ fontSize: 9, color: C.dim, marginTop: 1 }}>{yr < 2049 ? `${yr - 1972}/${yr - 1974}` : '—'}</div>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', gap: 12, marginTop: 14, flexWrap: 'wrap' }}>
          {[['≤2031', C.green], ['2032–34', '#86EFAC'], ['2035–37', C.amb], ['2038–40', '#FBBF24'], ['2041–44', C.org], ['2045+', C.red]].map(([l, c]) => (
            <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <div style={{ width: 10, height: 10, borderRadius: 2, background: c }} />
              <span style={{ fontSize: 11, color: C.dim }}>{l}</span>
            </div>
          ))}
        </div>
      </Card>

      <Card>
        <H2>Selected: {incK(income)}/yr{INC_LABEL[income] ? ' (' + INC_LABEL[income] + ')' : ''} · Joanne age {deathAge}</H2>
        <div style={{ display: 'grid', gridTemplateColumns: isNarrow ? '1fr 1fr' : 'repeat(4,1fr)', gap: 12 }}>
          {[
            ['Retire in', retYr >= 2049 ? '2049+' : retYr, retCol(retYr)],
            ["James's age", retYr < 2049 ? retYr - 1972 : '75+', C.blue],
            ["Joanne's age", retYr < 2049 ? retYr - 1974 : '75+', C.pink],
            ['Portfolio', retSnap ? fmtK(retSnap.total) : '—', C.gold],
          ].map(([l, v, c]) => (
            <div key={l} style={{ background: C.surf, borderRadius: 10, padding: '14px', textAlign: 'center' }}>
              <div style={{ fontSize: 9, color: C.lbl, textTransform: 'uppercase', letterSpacing: 1 }}>{l}</div>
              <div style={{ fontSize: 26, fontWeight: 700, color: c, fontFamily: "'DM Mono',monospace", marginTop: 6 }}>{v}</div>
            </div>
          ))}
        </div>
        {retSnap && (
          <div style={{ marginTop: 14, padding: '12px 14px', background: C.surf, borderRadius: 8, fontSize: 12, color: C.dim, lineHeight: 1.7 }}>
            <strong style={{ color: C.bright }}>Portfolio breakdown at {retYr}: </strong>
            James pension {fmt(retSnap.jPen)} · Joanne pension {fmt(retSnap.oPen)} · James ISA {fmt(retSnap.jISA)} · Joanne ISA {fmt(retSnap.oISA)} · Bonds {fmt(retSnap.jPB + retSnap.oPB)} · Company (net) {fmt(retSnap.cNet)}
          </div>
        )}
      </Card>
    </div>
  );
}
